import { addProductToList, all, removeProductFromList } from './api';
import { updateHeaderButtonCount } from './header';
import {
	getContainerClasses,
	isWishlistPage,
	toggleProductClasses,
	isSingleProduct,
	productHasImagesSlider,
	addNoImageSliderClass,
} from './helpers';
import {
	WISHLIST_CONTAINS_ITEM_CLASS,
	WISHLIST_DOES_NOT_CONTAIN_ITEM_CLASS,
	WISHLIST_IS_LOADING_CLASS,
} from './constants';

let wishlists = [];

const init = () => {
	// If there is no image slider on single product page, add a class to the wishlist button.
	if ( isSingleProduct() && ! productHasImagesSlider() ) {
		addNoImageSliderClass();
	}

	// Grab the Wishlist via AJAX.
	runAJAXActions();

	// Attach event listeners for the Wishlist buttons.
	maybeAddEventListeners();

	// Make sure we update the wishlist button classes when logged out visitor opens quickview.
	wp.hooks.addAction( 'qalaBuy.action.after_quickview_populated', 'wishlist_button_callback', () => {
		const containerClasses = getContainerClasses();
		toggleProductClasses( containerClasses, getAllWishlistProducts() );
	} );

	// Update heart visibility for Elastic Filters when results are updated
	wp.hooks.addAction( 'qalaElasticCore.action.onResult.results',
		'qec_wishlist_button_callback',
		() => {
			// Add a little delay so JS can do its thing and Elastic Filters as well
			setTimeout( () => {
				const containerClasses = '.qesf-main-container';
				toggleProductClasses( containerClasses, getAllWishlistProducts() );
				/**
				 * qalaWishlist.filter.loaderRemoveTimeout
				 *
				 * Filters the timeout of removing the loader overlay after the results have been returned from elasticsearch (but not yet rendered)
				 */
			}, wp.hooks.applyFilters( 'qalaWishlist.filter.loaderRemoveTimeout', 300 ) );
		},
		110
	);
};

/**
 * Get the combined number of products.
 *
 * @return {number} you KNOW it!
 */
function getNumberItemsInWishlist() {
	return wishlists.reduce( ( carry, { products } ) => carry + products.length, 0 );
}

/**
 * Get all Wishlist products.
 *
 * @return {Array} Flat array of all product IDs
 */
function getAllWishlistProducts() {
	return wishlists.reduce( ( previousProducts, { products } ) => [ ...previousProducts, ...products ], [] );
}

/**
 * Get the ID for the main Wishlist.
 *
 * At the moment there is only support for ONE
 * Wishlist in the frontend. There is however
 * support in the API for several.
 *
 * At some point in the future the idea is to
 * add the UI for creating multiple lists.
 *
 * @return {string|null} The Wishlist ID.
 */
function getMainWishlistId() {
	return wishlists.length ? wishlists[ 0 ].id : null;
}

/**
 * Append event listeners to the DOM.
 *
 */
function maybeAddEventListeners() {
	const containerClasses = getContainerClasses();
	const containers = document.querySelectorAll( containerClasses );
	if ( ! containers ) {
		return;
	}

	containers.forEach( container => {
		container.addEventListener( 'click', ( { target } ) => {
			if ( target.classList.contains( WISHLIST_DOES_NOT_CONTAIN_ITEM_CLASS ) ) {
				addProduct( target );
				return;
			}

			if ( target.classList.contains( WISHLIST_CONTAINS_ITEM_CLASS ) ) {
				removeProduct( target );
			}
		}, true );
	} );
}

/**
 * Add a product to the Wishlist.
 *
 * @param {HTMLElement} buttonElement the dom element.
 *
 * @return {void}
 */
async function addProduct( { dataset, classList } ) {
	const { productId } = dataset;
	const classLists = [ classList ];
	// Lets see if we can find more instances of this products wishlist button to add the classList edits to.
	const additionalbuttons = document.querySelectorAll( '.qala-wishlist-button[data-product-id="' + productId + '"]' );
	if ( additionalbuttons ) {
		additionalbuttons.forEach( button => {
			classLists.push( button.classList );
		} );
	}

	classLists.forEach( theClassList => {
		theClassList.add( WISHLIST_IS_LOADING_CLASS );
	} );

	const { data } = await addProductToList( getMainWishlistId(), productId );
	wishlists = [ ...data ];

	classLists.forEach( theClassList => {
		theClassList.remove( WISHLIST_DOES_NOT_CONTAIN_ITEM_CLASS );
		theClassList.remove( WISHLIST_IS_LOADING_CLASS );
		theClassList.add( WISHLIST_CONTAINS_ITEM_CLASS );
	} );

	updateHeaderButtonCount( getNumberItemsInWishlist() );
}

/**
 * Remove a product from the Wishlist.
 *
 * @param {HTMLElement} the dom element.
 *
 * @return {void}
 */
async function removeProduct( { dataset, classList } ) {
	const { productId } = dataset;
	const classLists = [ classList ];
	// Lets see if we can find more instances of this products wishlist button to add the classList edits to.
	const additionalbuttons = document.querySelectorAll( '.qala-wishlist-button[data-product-id="' + productId + '"]' );
	if ( additionalbuttons ) {
		additionalbuttons.forEach( button => {
			classLists.push( button.classList );
		} );
	}

	classLists.forEach( theClassList => {
		theClassList.add( WISHLIST_IS_LOADING_CLASS );
	} );

	const { data } = await removeProductFromList( getMainWishlistId(), productId );
	wishlists = [ ...data ];

	classLists.forEach( theClassList => {
		theClassList.remove( WISHLIST_CONTAINS_ITEM_CLASS );
		theClassList.remove( WISHLIST_IS_LOADING_CLASS );
		theClassList.add( WISHLIST_DOES_NOT_CONTAIN_ITEM_CLASS );
	} );

	if ( isWishlistPage() ) {
		const products = document.getElementsByClassName( `post-${ productId }` );
		if ( products.length ) {
			products[ 0 ].remove();
		}
	}

	updateHeaderButtonCount( getNumberItemsInWishlist() );
}

/**
 * Update the state of the buttons via AJAX
 * if the user is not logged in.
 *
 * This is to support full-page caching which
 * is what we tend to do around these parts :P
 *
 * @return {void}
 */
async function runAJAXActions() {
	const { data } = await all();
	if ( ! data || data.length <= 0 ) {
		return;
	}

	const containerClasses = getContainerClasses();
	wishlists = [ ...data ];
	updateHeaderButtonCount( getNumberItemsInWishlist() );
	toggleProductClasses( containerClasses, getAllWishlistProducts() );
}

/**
 * Adds compatibility with Qala Filters, making sure the buttons are updated as the filters have loaded.
 */
jQuery( 'body' ).on(
	'qala-filters-finished-loading',
	() => {
		const containerClasses = getContainerClasses();
		toggleProductClasses( containerClasses, getAllWishlistProducts() );
	}
);

export default init;
